import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectsApi } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

interface EditProjectModalProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onDelete: () => void;
}

const STAGE_KEYS = ['stages.idea', 'stages.mvp', 'stages.product'] as const;
const STAGE_VALUES = ['Только идея', 'Есть MVP', 'Работающий продукт'] as const;

export function EditProjectModal({
  project,
  open,
  onOpenChange,
  onSuccess,
  onDelete,
}: EditProjectModalProps) {
  const { t } = useTranslation('dashboard');

  const [formData, setFormData] = useState({
    name: '',
    market: t('createModal.marketDefault'),
    stage: 'Только идея' as Project['stage'],
    revenue: '0',
    description: '',
  });

  const [errors, setErrors] = useState<{ name?: boolean; description?: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Заполняем форму данными проекта при открытии
  useEffect(() => {
    if (project && open) {
      setFormData({
        name: project.name || '',
        market: project.market || t('createModal.marketDefault'),
        stage: project.stage || 'Только идея',
        revenue: project.revenue || '0',
        description: project.description || '',
      });
      setErrors({});
      setSubmitError('');
    }
  }, [project, open, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!project) return;

    // Валидация
    const newErrors: { name?: boolean; description?: boolean } = {};
    if (!formData.name.trim()) {
      newErrors.name = true;
    }
    if (!formData.description.trim()) {
      newErrors.description = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await projectsApi.update(project.id, {
        name: formData.name.trim(),
        market: formData.market.trim() || t('createModal.marketDefault'),
        stage: formData.stage,
        revenue: formData.revenue.trim() || '0',
        description: formData.description.trim(),
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update project:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { message?: string })?.message || t('editModal.unknownError');
      setSubmitError(t('editModal.errorSave', { error: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!project) return;

    const confirmed = window.confirm(
      t('editModal.deleteConfirm', { name: project.name })
    );

    if (!confirmed) return;

    setIsDeleting(true);
    setSubmitError('');

    try {
      await projectsApi.delete(project.id);
      onDelete();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete project:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { message?: string })?.message || t('editModal.unknownError');
      setSubmitError(t('editModal.errorDelete', { error: errorMessage }));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] p-8">
        <DialogHeader className="text-center">
          <DialogTitle className="text-[24px] font-bold text-center">
            {t('editModal.title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {submitError && (
            <div className="rounded-xl bg-red-50 p-3 text-[14px] text-red-600">
              {submitError}
            </div>
          )}

          {/* Название */}
          <div className="space-y-2">
            <Label htmlFor="edit-name" className="text-[14px] text-[#565858]">
              {t('editModal.nameLabel')}
            </Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: false });
              }}
              placeholder={t('editModal.namePlaceholder')}
              className={`h-12 rounded-xl border-[#eeeeee] ${
                errors.name ? 'border-red-500 focus:border-red-500' : ''
              }`}
              disabled={isSubmitting || isDeleting}
            />
          </div>

          {/* Рынок */}
          <div className="space-y-2">
            <Label htmlFor="edit-market" className="text-[14px] text-[#565858]">
              {t('editModal.marketLabel')}
            </Label>
            <Input
              id="edit-market"
              value={formData.market}
              onChange={(e) => setFormData({ ...formData, market: e.target.value })}
              className="h-12 rounded-xl border-[#eeeeee]"
              disabled={isSubmitting || isDeleting}
            />
          </div>

          {/* Стадия развития */}
          <div className="space-y-2">
            <Label htmlFor="edit-stage" className="text-[14px] text-[#565858]">
              {t('editModal.stageLabel')}
            </Label>
            <Select
              value={formData.stage}
              onValueChange={(value) =>
                setFormData({ ...formData, stage: value as Project['stage'] })
              }
              disabled={isSubmitting || isDeleting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGE_VALUES.map((stage, idx) => (
                  <SelectItem key={stage} value={stage}>
                    {t(STAGE_KEYS[idx] as string)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[12px] text-[#8c8c8c]">
              {t('editModal.stageHint')}
            </p>
          </div>

          {/* Доход в месяц */}
          <div className="space-y-2">
            <Label htmlFor="edit-revenue" className="text-[14px] text-[#565858]">
              {t('editModal.revenueLabel')}
            </Label>
            <Input
              id="edit-revenue"
              value={formData.revenue}
              onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
              className="h-12 rounded-xl border-[#eeeeee]"
              disabled={isSubmitting || isDeleting}
            />
            <p className="text-[12px] text-[#8c8c8c]">
              {t('editModal.revenueHint')}
            </p>
          </div>

          {/* Описание */}
          <div className="space-y-2">
            <Label htmlFor="edit-description" className="text-[14px] text-[#565858]">
              {t('editModal.descriptionLabel')}
            </Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description) setErrors({ ...errors, description: false });
              }}
              placeholder={t('editModal.descriptionPlaceholder')}
              rows={4}
              className={`rounded-xl border-[#eeeeee] resize-none ${
                errors.description ? 'border-red-500 focus:border-red-500' : ''
              }`}
              disabled={isSubmitting || isDeleting}
            />
          </div>

          {/* Удалить проект */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={isSubmitting || isDeleting}
            className="font-segoe text-[14px] leading-[20px] text-[#ff5b63] hover:text-[#dc2626] transition-colors text-left disabled:opacity-50"
          >
            {isDeleting ? t('editModal.deleting') : t('editModal.delete')}
          </button>

          {/* Кнопки */}
          <div className="flex gap-[24px]">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isDeleting}
              className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors text-center"
            >
              {t('editModal.close')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isDeleting}
              className="flex-1 bg-[#2197f1] text-white px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:opacity-90 transition-opacity text-center"
            >
              {isSubmitting ? t('editModal.submitting') : t('editModal.submit')}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
